import * as yup from "yup";
import moment from "moment";
import { minValidDate } from "../../DataEntry/Grid/gridFunctions";

export const eobFormCorresValidationSchema = (contextValues = {}) => {
  const { jobData,isSubJob, isFirstJob, isAdditional, isAdditionalInfoOptions, totalPages, isPayerPage } = contextValues;

  return yup.object().shape({
     overall_page_from: yup.string()
      .when("qdx_status", {
        is: (val) => val !== "INCOMPLETE",
        then: (schema) => 
          jobData?.client_level_customization?.overall_page_from
          ?  schema
          .required("Invalid Page Number")
          .test("overall_page_from_validation", "Invalid Page Number", function(value) {
            const { overall_page_to } = this.parent;
            const { totalPages } = this.options.context || {};
            if(!jobData?.client_level_customization?.overall_page_from){return true }

            // Check if value is empty, zero, or invalid
            if (!value || value <= 0) {
              return false;
            }
            
            // Check if exceeds total pages
            if (value > totalPages) {
              return false;
            }
            
            // Check if from is greater than to
            if (overall_page_to && parseInt(value) > parseInt(overall_page_to)) {
              return false;
            }
            
            return true;
          })
          :schema
      }),

    overall_page_to: yup.string()
      .when("qdx_status", {
        is: (val) => val !== "INCOMPLETE",
        then: (schema) => 
          jobData?.client_level_customization?.overall_page_from
          ? schema
          .required("Invalid Page Number")
          .test("overall_page_to_validation", "Invalid Page Number", function(value) {
            const { overall_page_from } = this.parent;
            const { totalPages } = this.options.context || {};
            if(!jobData?.client_level_customization?.overall_page_to){return true }
            // Check if value is empty, zero, or invalid
            if (!value || value <= 0) {
              return false;
            }
            
            // Check if exceeds total pages  
            if (value > totalPages) {
              return false;
            }
            
            // Check if from is greater than to
            if (overall_page_from && parseInt(overall_page_from) > parseInt(value)) {
              return false;
            }
            
            return true;
          })
          :schema
      }),
    payer_name: yup.string().when(["qdx_status", "$isSubJob", "$isFirstJob"], {
      is: (qdxStatus, isSub, isFirst) => qdxStatus !== "INCOMPLETE" && !(isSub && !isFirst),
      then: (schema) => schema.required("Required"),
    }),

    letter_date: yup.string().when(["qdx_status", "$isSubJob", "$isFirstJob"], {
      is: (qdxStatus, isSub, isFirst) => qdxStatus !== "INCOMPLETE" && !(isSub && !isFirst),
      then: (schema) =>
        schema.test("date-validation", function (value) {
          const cleanDate = value?.replace("__/__/____", "");

          if (!cleanDate) {
            return this.createError({ message: "Required" });
          }
          if (!moment(cleanDate, "MM/DD/YYYY", true).isValid() || !moment(cleanDate, "MM/DD/YYYY", true).isAfter(minValidDate)) {
            return this.createError({ message: "Invalid date" });
          }
          return true;
        }),
    }),

    correspondance: yup.string().when(["qdx_status", "$isSubJob", "$isFirstJob"], {
      is: (qdxStatus, isSub, isFirst) => qdxStatus !== "INCOMPLETE" && !(isSub && !isFirst),
      then: (schema) =>
        schema.test("correspondance-validation", "select an option", (value) => value && value !== "0"),
    }),

    qdx_status: yup.string().when("$isAdditional", {
      is: true,
      then: (schema) => schema.when("$isAdditionalInfoOptions.status", {
        is: true,
        then: (schema) => schema.required("Required")
      })
    }),
    
    qdx_reason: yup
        .array()
        .of(yup.number().typeError("Must be a number"))
        .when(["qdx_status", "$isAdditional"], {
        is: (status, isAdditional) => isAdditional && status === "INCOMPLETE",
        then: (schema) =>
            schema.when("$isAdditionalInfoOptions.reason", {
            is: true,
            then: (schema) =>
                schema.min(1, "At least one reason is required"), // must contain numbers
            otherwise: (schema) => schema, // allow empty array
            }),
        otherwise: (schema) => schema, // allow empty array
        }),

    payer_page_from: yup.string().when("$isPayerPage", {
      is: (isPayerPage) => isPayerPage && totalPages > 0,
      then: (schema) => schema.test("payer-page-validation", "Invalid Page Number", function(value) {
        const { payer_page_to } = this.parent;
        return value > 0 && value <= totalPages && value <= payer_page_to;
      }),
    }),

    payer_page_to: yup.string().when("$isPayerPage", {
      is: (isPayerPage) => isPayerPage && totalPages > 0,
      then: (schema) => schema.test("payer-page-validation", "Invalid Page Number", function(value) {
        const { payer_page_from } = this.parent;
        return value > 0 && value <= totalPages && payer_page_from <= value;
      }),
    }),

    claims: yup.array().of(
      yup.object().shape({
        period_start: yup.string().test("period-start-validation", function (value) {
          const { qdx_status } = this.options?.from?.[1]?.value;;
            
          if (qdx_status === "INCOMPLETE") return true;
          const cleanDate = value?.replace("__/__/____", "");

          if (!cleanDate) return this.createError({ message: "Required" });
          if (!moment(cleanDate, "MM/DD/YYYY", true).isValid() || !moment(cleanDate, "MM/DD/YYYY", true).isAfter(minValidDate)) {
            return this.createError({ message: "Invalid date" });
          }
          return true;
        }),

        period_end: yup.string().test("period-end-validation", function (value) {
          const { qdx_status } = this.options?.from?.[1]?.value;;
            
          if (qdx_status === "INCOMPLETE") return true;
          const cleanDate = value?.replace("__/__/____", "");

          if (!cleanDate) return this.createError({ message: "Required" });
          if (!moment(cleanDate, "MM/DD/YYYY", true).isValid() || !moment(cleanDate, "MM/DD/YYYY", true).isAfter(minValidDate)) {
            return this.createError({ message: "Invalid date" });
          }
          return true;
        }),

        page_from: yup.string().test("page-from-validation", "Invalid page", function (value) {
          const { qdx_status } = this.options?.from?.[1]?.value;;
          if (qdx_status === "INCOMPLETE") return true;
          const { page_to } = this.parent;
          if (totalPages > 0) {
            return value > 0 && value <= totalPages && value <= page_to;
          }
          return true;
        }),

        page_to: yup.string().test("page-to-validation", "Invalid page", function (value) {
          const { qdx_status } = this.options?.from?.[1]?.value;;
          if (qdx_status === "INCOMPLETE") return true;
          const { page_from } = this.parent;
          if (totalPages > 0) {
            return value > 0 && value <= totalPages && page_from <= value;
          }
          return true;
        }),
        patient_account_number: yup.string().test("patient_account_number-validation", function (value) {
          const { qdx_status } = this.options?.from?.[1]?.value;;
            
          if (qdx_status === "INCOMPLETE") return true;

          if (!value) return this.createError({ message: "Required" });
          return true;
        }),
        

      })
    ),
  });
};